import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';
import { showToast } from '../components/Toast';
import ConfirmModal from '../components/ConfirmModal';
import { DSCard, DSStatTile, DSIcon, DSButton } from '../design-system/primitives';
import StatusBadge from '../components/StatusBadge';
import './SquareIntegration.css';

function SquareIntegration() {
  const [status, setStatus] = useState(null);
  const [syncedUsers, setSyncedUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState('');
  const [syncResults, setSyncResults] = useState(null);
  const [confirmState, setConfirmState] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');
      
      // Load Square status
      const statusResponse = await apiService.getSquareStatus();
      setStatus(statusResponse.data);

      // If configured, load synced users
      if (statusResponse.data.configured) {
        const usersResponse = await apiService.getSquareUsers();
        setSyncedUsers(usersResponse.data);
      }
    } catch (err) {
      setError('Failed to load Square integration data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSync = () => {
    setConfirmState({
      title: 'Run Automated Sync',
      message: 'This will run the automated sync (customers, subscriptions, and invoices). Continue?',
      confirmLabel: 'Continue',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          setSyncing(true);
          setError('');
          setSyncResults(null);

          const response = await apiService.syncSquareAuto();
          setSyncResults(response.data.results);

          // Reload synced users and status
          const usersResponse = await apiService.getSquareUsers();
          setSyncedUsers(usersResponse.data);

          const statusResponse = await apiService.getSquareStatus();
          setStatus(statusResponse.data);

          showToast('Automated sync completed!', 'success');
        } catch (err) {
          const errorMsg = err.response?.data?.error || 'Failed to run automated sync';
          setError(errorMsg);
          console.error(err);
        } finally {
          setSyncing(false);
        }
      }
    });
  };

  const handleSyncInvoices = () => {
    setConfirmState({
      title: 'Sync Invoices',
      message: 'This will sync all paid invoices from Square to populate payment history. Continue?',
      confirmLabel: 'Continue',
      onConfirm: async () => {
        setConfirmState(null);
        try {
          setSyncing(true);
          setError('');
          setSyncResults(null);

          const response = await apiService.syncSquareInvoices();
          setSyncResults(response.data.results);

          // Reload synced users
          const usersResponse = await apiService.getSquareUsers();
          setSyncedUsers(usersResponse.data);

          showToast(`Invoice sync completed! ${response.data.results.processed} invoices processed`, 'success');
        } catch (err) {
          const errorMsg = err.response?.data?.error || 'Failed to sync invoices';
          setError(errorMsg);
          console.error(err);
        } finally {
          setSyncing(false);
        }
      }
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  if (loading) {
    return (
      <div className="loading">Loading Square integration...</div>
    );
  }

  return (
    <>
      {error && <div className="alert alert-error">{error}</div>}

      {/* Status Card */}
      <DSCard title="Integration Status" cornerVariant="lg">
        {status ? (
          <div className="status-info">
            <div className="status-row">
              <span className="status-label">Status:</span>
              <StatusBadge variant={status.configured ? 'success' : 'error'}>
                {status.configured ? 'Configured' : 'Not Configured'}
              </StatusBadge>
            </div>
            <div className="status-row">
              <span className="status-label">Environment:</span>
              <StatusBadge variant={status.environment === 'production' ? 'warning' : 'info'}>
                {status.environment || 'sandbox'}
              </StatusBadge>
            </div>
            {status.autoSync && (
              <>
                <div className="status-row">
                  <span className="status-label">Auto-Sync:</span>
                  <StatusBadge variant={status.autoSync.enabled ? 'success' : 'neutral'}>
                    {status.autoSync.enabled ? 'Enabled' : 'Disabled'}
                  </StatusBadge>
                </div>
                {status.autoSync.enabled && (
                  <>
                    <div className="status-row">
                      <span className="status-label">Schedule:</span>
                      <span>{status.autoSync.schedule}</span>
                    </div>
                    <div className="status-row">
                      <span className="status-label">Last Sync:</span>
                      <span>{status.autoSync.lastSyncTime ? formatDate(status.autoSync.lastSyncTime) : 'Never'}</span>
                    </div>
                    {status.autoSync.syncInProgress && (
                      <div className="alert alert-info" style={{ marginTop: '10px' }}>
                        <DSIcon name="clock" /> Sync currently in progress...
                      </div>
                    )}
                  </>
                )}
              </>
            )}
            {!status.configured && (
              <div className="alert alert-warning" style={{ marginTop: '20px' }}>
                <strong>Square is not configured.</strong>
                <p>To enable Square integration, add your Square access token to the server environment variables:</p>
                <code>SQUARE_ACCESS_TOKEN=your-token-here</code>
                <p style={{ marginTop: '10px' }}>See the <a href="https://developer.squareup.com/apps" target="_blank" rel="noopener noreferrer">Square Developer Dashboard</a> to get your access token.</p>
              </div>
            )}
          </div>
        ) : (
          <p>Unable to load status</p>
        )}
      </DSCard>

      <DSCard title="Square Tools" cornerVariant="lg">
        <div className="sync-actions">
          <Link to="/square-catalog-config" className="button button-secondary">
            <DSIcon name="package" /> Square Catalog
          </Link>
          <Link to="/square-sync-history" className="button button-secondary">
            <DSIcon name="refresh" /> Sync History
          </Link>
        </div>
      </DSCard>

      {status?.configured && (
        <>
          {/* Sync Actions Card */}
          <DSCard title="Sync Actions" cornerVariant="lg">
            <div className="sync-actions">
              {status?.autoSync?.enabled && (
                <DSButton tone="primary" appearance="solid" onClick={handleAutoSync} disabled={syncing || status?.autoSync?.syncInProgress}>
                  {syncing || status?.autoSync?.syncInProgress ? 'Syncing...' : <><DSIcon name="bolt" /> Run Auto-Sync Now</>}
                </DSButton>
              )}
              <DSButton tone="secondary" appearance="solid" onClick={handleSyncInvoices} disabled={syncing}>
                {syncing ? 'Syncing...' : <><DSIcon name="dollar" /> Sync Invoices</>}
              </DSButton>
              <DSButton tone="secondary" appearance="solid" onClick={loadData} disabled={syncing} leadingIcon={<DSIcon name="refresh" />}>Refresh Data
              </DSButton>
            </div>

            {syncResults && (
              <div className="sync-results" style={{ marginTop: '20px' }}>
                <h3>Last Sync Results</h3>
                {syncResults.startTime && (
                  <p style={{ marginBottom: '10px' }}>
                    Started: {formatDate(syncResults.startTime)} | Duration: {syncResults.duration?.toFixed(1)}s
                  </p>
                )}
                
                {/* Show automated sync results with both customers and subscriptions */}
                {syncResults.customers && syncResults.subscriptions ? (
                  <>
                    <h4>Customers</h4>
                    <div className="sqi-stats-grid">
                      <DSStatTile value={syncResults.customers.total} label="Total" />
                      <DSStatTile tone="success" value={syncResults.customers.created} label="Created" />
                      <DSStatTile tone="success" value={syncResults.customers.updated} label="Updated" />
                      <DSStatTile tone="warning" value={syncResults.customers.skipped} label="Skipped" />
                    </div>

                    <h4 style={{ marginTop: '15px' }}>Subscriptions</h4>
                    <div className="sqi-stats-grid">
                      <DSStatTile value={syncResults.subscriptions.total} label="Total" />
                      <DSStatTile tone="success" value={syncResults.subscriptions.matched} label="Matched" />
                      <DSStatTile tone="warning" value={syncResults.subscriptions.unmatched} label="Unmatched" />
                    </div>

                    {syncResults.invoices && (
                      <>
                        <h4 style={{ marginTop: '15px' }}>Invoices (Payment History)</h4>
                        <div className="sqi-stats-grid">
                          <DSStatTile value={syncResults.invoices.total} label="Total" />
                          <DSStatTile tone="success" value={syncResults.invoices.processed} label="Processed" />
                          <DSStatTile tone="warning" value={syncResults.invoices.skipped} label="Skipped" />
                          <DSStatTile tone="danger" value={syncResults.invoices.errors?.length || 0} label="Errors" />
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  /* Show single sync results (customers or subscriptions) */
                  <div className="sqi-stats-grid">
                    <DSStatTile value={syncResults.total} label={`Total ${syncResults.created !== undefined ? 'Customers' : 'Subscriptions'}`} />
                    {syncResults.created !== undefined ? (
                      <>
                        <DSStatTile tone="success" value={syncResults.created} label="Created" />
                        <DSStatTile tone="success" value={syncResults.updated} label="Updated" />
                        <DSStatTile tone="warning" value={syncResults.skipped} label="Skipped" />
                      </>
                    ) : (
                      <>
                        <DSStatTile tone="success" value={syncResults.matched} label="Matched Users" />
                        <DSStatTile tone="warning" value={syncResults.unmatched} label="Unmatched" />
                      </>
                    )}
                    <DSStatTile tone="danger" value={syncResults.errors?.length || 0} label="Errors" />
                  </div>
                )}

                {syncResults.errors && syncResults.errors.length > 0 && (
                  <div className="alert alert-error" style={{ marginTop: '15px' }}>
                    <strong>Errors during sync:</strong>
                    <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {syncResults.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>
                          {err.invoiceId ? `Invoice ${err.invoiceId}` : err.subscriptionId ? `Subscription ${err.subscriptionId}` : 'Unknown'}: {err.error}
                        </li>
                      ))}
                      {syncResults.errors.length > 10 && (
                        <li><em>... and {syncResults.errors.length - 10} more errors</em></li>
                      )}
                    </ul>
                  </div>
                )}
                
                {syncResults.invoices?.errors && syncResults.invoices.errors.length > 0 && (
                  <div className="alert alert-error" style={{ marginTop: '15px' }}>
                    <strong>Invoice Sync Errors ({syncResults.invoices.errors.length}):</strong>
                    <ul style={{ maxHeight: '300px', overflowY: 'auto' }}>
                      {syncResults.invoices.errors.slice(0, 10).map((err, idx) => (
                        <li key={idx}>
                          Invoice {err.invoiceId}: {err.error}
                        </li>
                      ))}
                      {syncResults.invoices.errors.length > 10 && (
                        <li><em>... and {syncResults.invoices.errors.length - 10} more errors</em></li>
                      )}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </DSCard>

          {/* Synced Users Summary */}
          <DSCard title="Synced Users Summary" cornerVariant="lg">
            <div className="sqi-stats-grid">
              <DSStatTile value={syncedUsers.length} label="Total Synced Users" />
              <DSStatTile tone="success" value={syncedUsers.filter(u => u.subscription_status === 'ACTIVE').length} label="Active Subscriptions" />
              <DSStatTile tone="warning" value={syncedUsers.filter(u => !u.square_subscription_id).length} label="No Subscription" />
            </div>
          </DSCard>

        </>
      )}

      {confirmState && (
        <ConfirmModal
          title={confirmState.title}
          message={confirmState.message}
          confirmLabel={confirmState.confirmLabel}
          danger={confirmState.danger}
          submitting={syncing}
          onConfirm={confirmState.onConfirm}
          onCancel={() => setConfirmState(null)}
        />
      )}
    </>
  );
}

export default SquareIntegration;
